export class TemplateSyntaxError extends Error {
    line;
    constructor(message, line) {
        super(`Line ${line}: ${message}`);
        this.line = line;
        this.name = 'TemplateSyntaxError';
    }
}
const PATH = /^(\.|[A-Za-z_$][\w$-]*(\.[\w$-]+)*)$/;
/** A path a placeholder or section may name. None can contain `>`. */
export const isTemplatePath = (path) => PATH.test(path);
const TAG = /\{\{(\{?)([^{}]*?)\}\}(\}?)/g;
function lineAt(template, index) {
    let line = 1;
    for (let i = 0; i < index; i++)
        if (template.charCodeAt(i) === 10)
            line++;
    return line;
}
function parse(template) {
    const root = [];
    const stack = [
        { node: null, children: root },
    ];
    let cursor = 0;
    for (const match of template.matchAll(TAG)) {
        const [whole, openBrace, rawBody, closeBrace] = match;
        const index = match.index;
        const line = lineAt(template, index);
        const top = stack[stack.length - 1];
        if (index > cursor)
            top.children.push({ kind: 'text', text: template.slice(cursor, index) });
        cursor = index + whole.length;
        if (openBrace || closeBrace)
            throw new TemplateSyntaxError('raw output ({{{...}}}) is not allowed', line);
        const body = rawBody.trim();
        const sigil = body[0];
        const path = '#^/'.includes(sigil) ? body.slice(1).trim() : body;
        if (!PATH.test(path))
            throw new TemplateSyntaxError(`"{{${body}}}" is not a placeholder this template supports`, line);
        if (sigil === '#' || sigil === '^') {
            const node = {
                kind: sigil === '#' ? 'section' : 'inverse',
                path,
                line,
                children: [],
            };
            top.children.push(node);
            stack.push({ node, children: node.children });
        }
        else if (sigil === '/') {
            if (!top.node || top.node.path !== path) {
                throw new TemplateSyntaxError(`"{{/${path}}}" closes a section that is not open`, line);
            }
            stack.pop();
        }
        else {
            const assigned = /=\s*("?)$/.exec(template.slice(Math.max(0, index - 16), index));
            const asWritten = assigned ? !assigned[1] || template[index + whole.length] === '"' : false;
            top.children.push({ kind: 'value', path, line, asWritten });
        }
    }
    if (cursor < template.length)
        stack[stack.length - 1].children.push({ kind: 'text', text: template.slice(cursor) });
    const open = stack[stack.length - 1].node;
    if (open)
        throw new TemplateSyntaxError(`"{{#${open.path}}}" is never closed`, open.line);
    return root;
}
export function resolvePath(data, path) {
    if (path === '.')
        return data;
    let value = data;
    for (const key of path.split('.')) {
        if (value === null || typeof value !== 'object')
            return undefined;
        value = value[key];
    }
    return value;
}
function lookup(contexts, path) {
    if (path === '.')
        return contexts[contexts.length - 1];
    const head = path.split('.')[0];
    for (let i = contexts.length - 1; i >= 0; i--) {
        const context = contexts[i];
        if (context !== null && typeof context === 'object' && head in context)
            return resolvePath(context, path);
    }
    return undefined;
}
const ESCAPES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
function escapeText(text) {
    return text.replace(/[&<>"']/g, (char) => ESCAPES[char]);
}
function isEmpty(value) {
    return (value === undefined ||
        value === null ||
        value === false ||
        (Array.isArray(value) && value.length === 0));
}
/** Below this a number prints as written, so a year never reads as 2,026. */
const GROUPED_FROM = 10_000;
/** Where a missing value printed nothing. A value is escaped, so data can never produce it. */
const MISSING = '<mf-missing>';
const ACTION = /<mf-action\b[^>]*>[\s\S]*?<\/mf-action\s*>/g;
/**
 * An action whose prompt needs a missing value is dropped: the host finds no declaration for its
 * button and disables it, as it does for a missing row or filter value.
 */
const dropIncompleteActions = (html) => html
    .replace(ACTION, (action) => (action.includes(MISSING) ? '' : action))
    .replaceAll(MISSING, '');
/** How `{{path}}` prints a finite number. */
export function numberPrinter(locale) {
    const grouped = new Intl.NumberFormat(locale, { maximumFractionDigits: 20 });
    return (value) => (Math.abs(value) >= GROUPED_FROM ? grouped.format(value) : String(value));
}
/** `locale` defaults to the viewer's, as chart units do. */
export function renderWidget(document, data, locale) {
    const missing = new Set();
    const objects = new Set();
    const printNumber = numberPrinter(locale);
    const render = (nodes, contexts, scopes, iterations) => nodes
        .map((node) => {
        if (node.kind === 'text')
            return markRows(node.text, iterations);
        const value = lookup(contexts, node.path);
        if (node.kind === 'value') {
            if (isActionPath(node.path))
                return `{{${node.path}}}`;
            if (typeof value === 'string')
                return escapeText(value);
            if (typeof value === 'number') {
                if (Number.isFinite(value))
                    return node.asWritten ? String(value) : printNumber(value);
                missing.add(node.path);
                return MISSING;
            }
            if (typeof value === 'boolean')
                return String(value);
            if (value === undefined || value === null) {
                missing.add(node.path);
                return MISSING;
            }
            objects.add(node.path);
            return '';
        }
        const scope = scopes.length ? `${scopes[scopes.length - 1]}.${node.path}` : node.path;
        const inner = [...scopes, scope];
        if (node.kind === 'inverse')
            return isEmpty(value) ? render(node.children, contexts, inner, iterations) : '';
        if (isEmpty(value))
            return '';
        if (Array.isArray(value))
            return value
                .map((item, index) => render(node.children, [...contexts, item], inner, [
                ...iterations,
                { paths: [node.path, scope], index },
            ]))
                .join('');
        return render(node.children, [...contexts, value], inner, iterations);
    })
        .join('');
    return {
        html: dropIncompleteActions(render(parse(document), [data], [], [])),
        missing: [...missing],
        objects: [...objects],
    };
}
const DATA_ROW = /\sdata-row\s*=\s*"([^"]*)"/g;
/**
 * Gives each `data-row` of a list being repeated the index of its item, so the host maps a row to
 * its data even when the list is shown twice or a section inside it skips items. Only template text
 * is marked: a value is escaped, so it cannot carry an attribute.
 */
function markRows(text, iterations) {
    if (iterations.length === 0)
        return text;
    return text.replace(DATA_ROW, (attribute, dataset) => {
        const iteration = iterations.findLast((candidate) => candidate.paths.includes(dataset));
        return iteration ? `${attribute} data-row-index="${iteration.index}"` : attribute;
    });
}
/** Roots an action's prompt reads when it runs (the clicked row, the current filters), not data. */
export const ACTION_ROOTS = ['row', 'filter'];
function isActionPath(path) {
    return ACTION_ROOTS.includes(path.split('.')[0]);
}
/** Every data placeholder and section in the template, with its line; action-time paths left out. */
export function templatePlaceholders(document) {
    const found = [];
    const walk = (nodes, scopes) => {
        for (const node of nodes) {
            if (node.kind === 'text' || node.path === '.' || isActionPath(node.path))
                continue;
            found.push({ path: node.path, scopes, line: node.line });
            if (node.kind !== 'value') {
                const inner = scopes.length ? `${scopes[scopes.length - 1]}.${node.path}` : node.path;
                walk(node.children, [...scopes, inner]);
            }
        }
    };
    walk(parse(document), []);
    return found;
}
/** Every data path a template reads, sections prefixing the paths inside them. */
export function templatePaths(document) {
    return [
        ...new Set(templatePlaceholders(document).map(({ path, scopes }) => scopes.length ? `${scopes[scopes.length - 1]}.${path}` : path)),
    ];
}
